import { useEffect, useState } from 'react';
import { useTeamsContext } from './hooks/useTeamsContext.js';
import { SidePanel } from './components/SidePanel.js';
import { MeetingStage } from './components/MeetingStage.js';
import { AuthStatusBanner } from './components/AuthStatusBanner.js';
import { TrialBanner } from './components/TrialBanner.js';
import { LightMeetingsBanner } from './components/LightMeetingsBanner.js';

function isLocalDevHost(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h.startsWith('192.168.');
}

function isLightMeetingsPreviewRequested(): boolean {
  return new URLSearchParams(window.location.search).get('showLightMeetingsBanner') === '1';
}

/**
 * Config page shown when Teams loads the app for tab configuration.
 * Calls pages.config.setValidityState(true) to enable the Save button.
 */
function ConfigPage() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    async function setupConfig() {
      try {
        const { pages } = await import('@microsoft/teams-js');

        pages.config.registerOnSaveHandler((saveEvent) => {
          pages.config.setConfig({
            entityId: 'annoteer-tab',
            contentUrl: `${window.location.origin}/`,
            suggestedDisplayName: 'Annoteer',
          });
          saveEvent.notifySuccess();
        });

        pages.config.setValidityState(true);
        setReady(true);
      } catch (err) {
        console.error('[Config] Failed to set up config page:', err);
      }
    }
    setupConfig();
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#1a1a2e',
        color: '#e0e0e0',
        fontFamily: 'Segoe UI, system-ui, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h2 style={{ color: '#818cf8', marginBottom: 8 }}>Annoteer</h2>
        <p style={{ color: '#9ca3af', fontSize: 14 }}>
          Collaborative document annotation for Teams meetings
        </p>
        <p style={{ color: '#9ca3af', fontSize: 14 }}>
          {ready ? 'Click Save to add Annoteer to this meeting.' : 'Loading...'}
        </p>
      </div>
    </div>
  );
}

export function App() {
  const { frameContext, isInitialized, isDevMode } = useTeamsContext();

  if (!isInitialized) {
    return <div className="loading">Initializing Annoteer...</div>;
  }

  // Light-meetings / out-of-Teams path: when Teams SDK init failed and we're
  // not on a local dev host, the user is browsing the production URL outside
  // a Teams meeting (or arrived via the anonymous "light meetings" launcher
  // which strips meeting apps). Show the friendly explainer instead of the
  // app shell. ?showLightMeetingsBanner=1 forces this for dev preview.
  if ((isDevMode && !isLocalDevHost()) || isLightMeetingsPreviewRequested()) {
    return <LightMeetingsBanner />;
  }

  // Config mode — Teams is configuring the tab
  if (frameContext === 'settings') {
    return <ConfigPage />;
  }

  return (
    <div className="app">
      {isDevMode && <div className="app__dev-banner">Dev mode — not running inside Teams</div>}
      <AuthStatusBanner />
      <TrialBanner />
      {frameContext === 'meetingStage' ? <MeetingStage /> : <SidePanel />}
    </div>
  );
}
